import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { CalendarCheck, CalendarPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Header pill: shows "Connect Google Calendar" when not connected,
 * "✓ Calendar Synced" when connected (clicking it triggers a re-sync of upcoming bookings).
 */
const GoogleCalendarButton = () => {
  const [status, setStatus] = useState({ connected: false });
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => {
    fetch(`${API}/api/oauth/calendar/status`).then(r => r.json()).then(setStatus).catch(() => {});
  }, []);

  useEffect(() => {
    loadStatus();
    // Detect post-redirect success/failure
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal_connected') === '1') {
      toast.success('Google Calendar connected ✓');
      window.history.replaceState({}, '', window.location.pathname);
      loadStatus();
    } else if (params.get('gcal_error')) {
      toast.error(`Google Calendar: ${params.get('gcal_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadStatus]);

  const connect = () => {
    window.location.href = `${API}/api/oauth/calendar/start`;
  };

  const syncAll = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/oauth/calendar/sync-all`, { method: 'POST' });
      const d = await r.json();
      toast.success(`Synced ${d.synced} booking(s) · skipped ${d.skipped}`);
    } catch (e) { toast.error('Sync failed'); }
    setBusy(false);
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Future bookings will not auto-sync until you reconnect.')) return;
    await fetch(`${API}/api/oauth/calendar/disconnect`, { method: 'POST' });
    toast.success('Disconnected');
    setStatus({ connected: false });
  };

  if (!status.connected) {
    return (
      <Button
        size="sm"
        onClick={connect}
        variant="outline"
        className="border-blue-600/60 text-blue-400 hover:bg-blue-950/30 text-[10px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3"
        title="Sync ACE bookings to your Google Calendar"
        data-testid="connect-gcal-btn"
      >
        <CalendarPlus className="h-3 w-3 sm:mr-1" />
        <span className="hidden sm:inline">Connect Calendar</span>
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        onClick={syncAll}
        disabled={busy}
        variant="outline"
        className="border-emerald-600/60 text-emerald-400 hover:bg-emerald-950/30 text-[10px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3"
        title={`Connected as ${status.admin_email}. Click to re-sync upcoming bookings.`}
        data-testid="gcal-resync-btn"
      >
        {busy ? <Loader2 className="h-3 w-3 sm:mr-1 animate-spin" /> : <CalendarCheck className="h-3 w-3 sm:mr-1" />}
        <span className="hidden sm:inline">Calendar Synced</span>
      </Button>
      <button
        onClick={disconnect}
        className="text-[9px] text-gray-500 hover:text-red-400 hidden sm:inline"
        data-testid="gcal-disconnect-btn"
        title="Disconnect Google Calendar"
      >×</button>
    </div>
  );
};

export default GoogleCalendarButton;
